import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Pagination from "components/pagination";
import { impersonationLogin, impersonationLogout, listAccessRequests, listRequestLogs } from "./api";

const pick = (row, keys, fallback = "-") => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null && row?.[key] !== "") {
      return row[key];
    }
  }
  return fallback;
};

const resolveUserName = (row) =>
  pick(row, [
    "user_name",
    "userName",
    "name",
    "full_name",
    "fullName",
    "target_user_name",
    "targetUserName",
    "customer_name",
    "customerName",
  ]);

const resolveUserPhone = (row) =>
  pick(row, [
    "user_phone",
    "user_mobile",
    "phone",
    "mobile",
    "mobile_number",
    "mobileNumber",
    "contact_number",
    "contactNumber",
  ]);

const normalizeList = (res) => {
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  if (Array.isArray(res?.data?.requests)) return res.data.requests;
  if (Array.isArray(res?.requests)) return res.requests;
  return [];
};

const parseJsonOrEmpty = (value) => {
  const input = String(value || "").trim();
  if (!input) return {};
  return JSON.parse(input);
};

const RequestListPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [sessionStateById, setSessionStateById] = useState({});
  const [processingId, setProcessingId] = useState(null);
  const [logsOpen, setLogsOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [requestLogs, setRequestLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [extraPayloadById, setExtraPayloadById] = useState({});

  const loadRequests = async () => {
    setLoading(true);
    const res = await listAccessRequests();
    const parsed = normalizeList(res);
    setRows(parsed);

    if (res?.status !== 200) {
      toast.error(res?.message || "Failed to load requests");
    }
    setLoading(false);
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;

    return rows.filter((row) => {
      const id = String(pick(row, ["id", "request_id", "requestId"], "")).toLowerCase();
      const userId = String(pick(row, ["user_id", "userId", "target_user_id"], "")).toLowerCase();
      const userName = String(resolveUserName(row)).toLowerCase();
      const userPhone = String(resolveUserPhone(row)).toLowerCase();
      const status = String(pick(row, ["status"], "")).toLowerCase();
      const reason = String(pick(row, ["reason", "remark", "comment"], "")).toLowerCase();
      return (
        id.includes(query) ||
        userId.includes(query) ||
        userName.includes(query) ||
        userPhone.includes(query) ||
        status.includes(query) ||
        reason.includes(query)
      );
    });
  }, [rows, search]);

  const totalRecords = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / recordsPerPage));
  const startIndex = (currentPage - 1) * recordsPerPage;
  const paginatedRows = filteredRows.slice(startIndex, startIndex + recordsPerPage);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const isAcceptedByUser = (row) => {
    const status = String(pick(row, ["status"], "")).toLowerCase();
    return status === "approved" || status === "accepted" || status === "granted";
  };

  const deriveSessionFromRow = (row) => {
    const explicit = pick(row, ["is_logged_in", "isLoggedIn", "session_active", "is_active"], null);
    if (explicit !== null && explicit !== "-") {
      return explicit === true || explicit === 1 || explicit === "1" || explicit === "true";
    }

    const loginAt = pick(row, ["login_at", "loginAt"], "");
    const logoutAt = pick(row, ["logout_at", "logoutAt"], "");
    return Boolean(loginAt && loginAt !== "-" && (!logoutAt || logoutAt === "-"));
  };

  const isLoggedIn = (row) => {
    const requestId = pick(row, ["id"], null);
    if (requestId && Object.prototype.hasOwnProperty.call(sessionStateById, requestId)) {
      return Boolean(sessionStateById[requestId]?.loggedIn);
    }
    return deriveSessionFromRow(row);
  };

  const resolveLoginResponse = (res) => {
    const token =
      pick(res?.data, ["grant_token", "grantToken", "session_token", "sessionToken", "token"], "") ||
      pick(res, ["grant_token", "grantToken", "session_token", "sessionToken"], "");
    const url = pick(res?.data, ["redirect_url", "redirectUrl"], "") || pick(res, ["redirect_url", "redirectUrl"], "");
    return { token, url };
  };

  const handleLoginLogout = async (row) => {
    const requestId = pick(row, ["id", "request_id", "requestId"], null);
    if (!requestId) {
      toast.error("Request id missing");
      return;
    }

    let extraPayload = {};
    try {
      extraPayload = parseJsonOrEmpty(extraPayloadById[requestId]);
    } catch (error) {
      toast.error(`Invalid JSON for request ${requestId}`);
      return;
    }

    if (!isAcceptedByUser(row)) {
      toast.error("Login is available only after user accepts request");
      return;
    }

    const loggedIn = isLoggedIn(row);
    setProcessingId(requestId);

    if (!loggedIn) {
      const res = await impersonationLogin({
        request_id: Number(requestId),
        redirect_url: "https://seeb.in/admin-user-access-login",
        ...extraPayload,
      });

      if (res?.status === 200 || res?.status === 201) {
        const { token, url } = resolveLoginResponse(res);
        setSessionStateById((prev) => ({
          ...prev,
          [requestId]: { loggedIn: true, sessionToken: token || "" },
        }));
        toast.success(res?.message || "Login grant created");
        if (url) {
          window.open(url, "_blank");
          setProcessingId(null);
          return;
        }
        await loadRequests();
      } else {
        toast.error(res?.message || "Failed to create login grant");
      }
      setProcessingId(null);
      return;
    }

    const res = await impersonationLogout({
      request_id: Number(requestId),
      ...extraPayload,
    });

    if (res?.status === 200) {
      setSessionStateById((prev) => ({
        ...prev,
        [requestId]: { loggedIn: false, sessionToken: "" },
      }));
      toast.success(res?.message || "Logged out successfully");
      await loadRequests();
    } else {
      toast.error(res?.message || "Logout failed");
    }
    setProcessingId(null);
  };

  const normalizeLogs = (res) => {
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.data?.data)) return res.data.data;
    if (Array.isArray(res?.data?.logs)) return res.data.logs;
    if (Array.isArray(res?.logs)) return res.logs;
    return [];
  };

  const handleOpenLogs = async (row) => {
    // const requestId = row.id;
    if (!row.id) {
      toast.error("Request id missing");
      return;
    }

    setSelectedRow(row);
    setLogsOpen(true);
    setLogsLoading(true);

    const res = await listRequestLogs(row.id);

    if (res?.status === 200) {
      setRequestLogs(res.data);
    } else {
      setRequestLogs([]);
      toast.error(res?.message || "Failed to load logs");
    }
    setLogsLoading(false);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Toaster position="top-center" reverseOrder={false} />

      <div className="mt-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">User Access Requests</h2>
            <p className="mt-1 text-xs text-gray-500">Manage user access requests</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/user-access/requests/add")}
              className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Add Request
            </button>
            <button
              type="button"
              onClick={loadRequests}
              className="rounded bg-gray-700 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="mt-4">
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            placeholder="Search by id, name, phone, status, reason"
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm md:w-96"
          />
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-600">
              <tr>
                <th className="px-3 py-2">Sr No</th>
                <th className="px-3 py-2">User Name</th>
                <th className="px-3 py-2">Phone</th>
                <th className="px-3 py-2">Target</th>
                <th className="px-3 py-2">Reason</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Created</th>
                <th className="px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-3 py-6 text-center text-gray-500">
                    Loading requests...
                  </td>
                </tr>
              ) : paginatedRows.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-3 py-6 text-center text-gray-500">
                    No requests found
                  </td>
                </tr>
              ) : (
                paginatedRows.map((row, index) => {
                  const requestId = pick(row, ["id", "request_id", "requestId"]);
                  const currentStatus = pick(row, ["status"], "pending");
                  const accepted = isAcceptedByUser(row);
                  const loggedIn = isLoggedIn(row);
                  const isProcessing = processingId === requestId;

                  return (
                    <tr key={requestId} className="border-b border-gray-100">
                      <td className="px-3 py-2">{startIndex + index + 1}</td>
                      <td className="px-3 py-2">{resolveUserName(row)}</td>
                      <td className="px-3 py-2">{resolveUserPhone(row)}</td>
                      <td className="px-3 py-2">{pick(row, ["target_user_id", "targetUserId", "subject_user_id"])}</td>
                      <td className="px-3 py-2">{pick(row, ["reason", "remark", "comment"])}</td>
                      <td className="px-3 py-2">{currentStatus}</td>
                      <td className="px-3 py-2">{pick(row, ["created_at", "createdAt", "request_date"])}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleLoginLogout(row)}
                            disabled={!accepted || isProcessing}
                            className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                          >
                            {isProcessing ? "Processing..." : loggedIn ? "Logout" : "Login"}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleOpenLogs(row)}
                            className="rounded bg-gray-700 px-3 py-1 text-xs font-medium text-white hover:bg-gray-800"
                          >
                            Logs
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(number) => setCurrentPage(number)}
            recordsPerPage={recordsPerPage}
            onRecordsPerPageChange={(value) => {
              setCurrentPage(1);
              setRecordsPerPage(value);
            }}
            totalRecords={totalRecords}
            count={paginatedRows.length}
            showFirstLastControls
            compact
          />
        </div>
      </div>

      {logsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-xl rounded-lg bg-white p-5 shadow-xl">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">Request Logs</h3>
              <button
                type="button"
                onClick={() => setLogsOpen(false)}
                className="rounded bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-200"
              >
                Close
              </button>
            </div>

            <div className="mt-4 max-h-[420px] overflow-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 text-left uppercase text-gray-600">
                  <tr>
                    <th className="px-2 py-2">Log ID</th>
                    <th className="px-2 py-2">Request</th>
                    <th className="px-2 py-2">Action</th>
                    <th className="px-2 py-2">Status</th>
                    <th className="px-2 py-2">Login At</th>
                    <th className="px-2 py-2">Logout At</th>
                    <th className="px-2 py-2">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {logsLoading ? (
                    <tr>
                      <td colSpan="7" className="px-2 py-6 text-center text-gray-500">
                        Loading logs...
                      </td>
                    </tr>
                  ) : requestLogs.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-2 py-6 text-center text-gray-500">
                        No logs found
                      </td>
                    </tr>
                  ) : (
                    requestLogs.map((log, index) => (
                      <tr key={`${pick(log, ["id", "log_id", "logId"], "log")}-${index}`} className="border-b border-gray-100">
                        <td className="px-2 py-2">{pick(log, ["id", "log_id", "logId"])}</td>
                        <td className="px-2 py-2">{pick(log, ["request_id", "requestId"])}</td>
                        <td className="px-2 py-2">{pick(log, ["action", "event", "type"])}</td>
                        <td className="px-2 py-2">{pick(log, ["status", "state"])}</td>
                        <td className="px-2 py-2">{pick(log, ["login_at", "loginAt", "start_time"])}</td>
                        <td className="px-2 py-2">{pick(log, ["logout_at", "logoutAt", "end_time"])}</td>
                        <td className="px-2 py-2">{pick(log, ["created_at", "createdAt"])}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default RequestListPage;
